import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField,
  Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Autocomplete, Switch, Checkbox, InputAdornment, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton, Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Workspaces as SchemesIcon, Add as AddIcon, Delete as DeleteIcon,
  Save as SaveIcon, Class as ClassIcon, AccountBalance as AccountIcon,
  Person as PersonIcon, Search as SearchIcon, Edit as EditIcon,
  CheckBox as CheckboxOnIcon, CheckBoxOutlineBlank as CheckboxOffIcon,
  RestartAlt as ResetIcon, Refresh as RefreshIcon, AutoFixHigh as ReapplyIcon,
  RedeemOutlined as OneTimeIcon, Undo as UndoIcon,
  DeleteForever as RemoveAccountIcon, Warning as WarningIcon,
  KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── Scheme editor dialog ──────────────────────────────────────
function SchemeDialog({ open, onClose, onSaved, scheme, individualAccounts }) {
  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive]           = useState(true);
  // [{ id?, account_name, sub_account, mode, percentage, amount }]  — value lives in
  // either `percentage` or `amount` depending on `mode`.
  const [lines, setLines]             = useState([]);
  const [saving, setSaving]           = useState(false);

  // Cache of sub-account lists per parent account so we don't re-fetch
  // on every keystroke. Loaded lazily as accounts are picked.
  const [subAccountsByAccount, setSubByAcc] = useState({}); // { account: string[] }

  useEffect(() => {
    if (!open) return;
    setName(scheme?.name || '');
    setDescription(scheme?.description || '');
    setActive(scheme ? !!scheme.active : true);
    setLines(scheme?.lines ? scheme.lines.map(l => ({
      id: l.id,
      account_name: l.account_name,
      sub_account:  l.sub_account || '',
      mode: l.mode || 'percentage',
      percentage: l.mode === 'amount' ? '' : (l.percentage ?? ''),
      amount:     l.mode === 'amount' ? (l.amount ?? '') : '',
    })) : []);
    setSubByAcc({});
  }, [open, scheme]);

  // Lazily load sub-accounts for an account when it's first seen.
  const loadSubsForAccount = async (account) => {
    if (!account || subAccountsByAccount[account] != null) return;
    try {
      const r = await API.get('/scholarships/sub-accounts', { params: { account } });
      setSubByAcc(prev => ({ ...prev, [account]: r.data || [] }));
    } catch (_) {
      setSubByAcc(prev => ({ ...prev, [account]: [] }));
    }
  };
  // When a scheme is loaded, prefetch sub-accounts for every distinct
  // account already on it so the picker is responsive.
  useEffect(() => {
    if (!open) return;
    const accounts = Array.from(new Set(lines.map(l => l.account_name).filter(Boolean)));
    accounts.forEach(loadSubsForAccount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheme]);

  const addLineRow = () => setLines(prev =>
    [...prev, { account_name: '', sub_account: '', mode: 'percentage', percentage: '', amount: '' }]);
  const setLineAt  = (i, patch) => setLines(prev => prev.map((l, idx) => {
    if (idx !== i) return l;
    const next = { ...l, ...patch };
    // Clear sub_account when account_name changes — old sub-accounts
    // don't necessarily exist under the new parent.
    if (patch.account_name !== undefined && patch.account_name !== l.account_name) {
      next.sub_account = '';
      if (patch.account_name) loadSubsForAccount(patch.account_name);
    }
    return next;
  }));
  const removeLineAt = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) return swal.error('Scheme name is required', 'Missing name');
    const cleaned = lines
      .filter(l => l.account_name && l.account_name.trim())
      .map(l => {
        const mode = l.mode || 'percentage';
        return {
          account_name: l.account_name.trim(),
          sub_account:  (l.sub_account || '').trim(),
          mode,
          percentage: mode === 'percentage' ? (Number(l.percentage) || 0) : 0,
          amount:     mode === 'amount'     ? (Number(l.amount)     || 0) : 0,
        };
      });
    if (cleaned.length === 0 && !scheme) {
      return swal.error('Add at least one account line for the scheme', 'No lines');
    }
    // A line is keyed by (account, sub_account). Whole-account = empty sub.
    const seen = new Set();
    for (const l of cleaned) {
      const key = `${l.account_name}::${l.sub_account}`;
      const label = l.sub_account ? `${l.account_name} / ${l.sub_account}` : l.account_name;
      if (seen.has(key)) {
        return swal.error(`Duplicate line for "${label}". One line per account or sub-account.`, 'Duplicate');
      }
      seen.add(key);
      if (l.mode === 'percentage' && (l.percentage < 0 || l.percentage > 100)) {
        return swal.error(`Percentage for "${label}" must be 0-100`, 'Bad percentage');
      }
      if (l.mode === 'amount' && l.amount < 0) {
        return swal.error(`Amount for "${label}" cannot be negative`, 'Bad amount');
      }
    }

    // Two scheme lines match if they share account_name AND sub_account.
    const matches = (a, b) => a.account_name === b.account_name
      && (a.sub_account || '') === (b.sub_account || '');
    const sameValue = (a, b) => a.mode === b.mode && (
      a.mode === 'percentage'
        ? Number(a.percentage) === Number(b.percentage)
        : Number(a.amount)     === Number(b.amount)
    );

    setSaving(true);
    try {
      if (scheme?.id) {
        await API.put(`/scholarships/schemes/${scheme.id}`, { name, description, active: active ? 1 : 0 });
        const existingIds = new Set(scheme.lines.map(l => l.id));
        const keptIds     = new Set();
        for (const l of cleaned) {
          const orig = scheme.lines.find(x => matches(x, l));
          if (orig) {
            keptIds.add(orig.id);
            if (!sameValue(orig, l)) {
              await API.put(`/scholarships/lines/${orig.id}`, {
                mode: l.mode, percentage: l.percentage, amount: l.amount,
              });
            }
          } else {
            await API.post(`/scholarships/schemes/${scheme.id}/lines`, {
              account_name: l.account_name,
              sub_account:  l.sub_account,
              mode: l.mode, percentage: l.percentage, amount: l.amount,
            });
          }
        }
        for (const id of existingIds) {
          if (!keptIds.has(id)) await API.delete(`/scholarships/lines/${id}`);
        }
      } else {
        await API.post('/scholarships/schemes', { name, description, lines: cleaned });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save scheme'));
    } finally { setSaving(false); }
  };

  // Summary stats for the footer
  const subLineCount   = lines.filter(l => l.sub_account && l.sub_account.trim()).length;
  const wholeLineCount = lines.length - subLineCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{
        py: 1.6, px: 2.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
        borderBottom: 1, borderColor: 'divider',
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
          color: 'primary.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <SchemesIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.1 }}>
            {scheme ? 'Edit Scholarship Scheme' : 'New Scholarship Scheme'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Set the discount rules; enrolled students inherit them on every new bill
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        {scheme && (
          <Chip size="small"
            color={active ? 'success' : 'default'} variant={active ? 'filled' : 'outlined'}
            label={active ? 'Active' : 'Inactive'}
            onClick={() => setActive(v => !v)}
            sx={{ fontWeight: 700, cursor: 'pointer' }} />
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5, bgcolor: 'grey.50' }}>
        <Stack spacing={2}>
          {/* Section 1 — Basic info */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%',
                bgcolor: 'primary.main', color: 'primary.contrastText',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
              }}>1</Box>
              <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                Basic information
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Stack spacing={1.6}>
                <TextField size="small" label="Scheme name" required value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Staff Children Bursary" fullWidth />
                <TextField size="small" label="Description" value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline minRows={2} fullWidth
                  placeholder="What is this scheme for? Who qualifies?" />
              </Stack>
            </Box>
          </Card>

          {/* Section 2 — Account lines */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexWrap: 'wrap' }}>
              <Box sx={{
                width: 22, height: 22, borderRadius: '50%',
                bgcolor: 'primary.main', color: 'primary.contrastText',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
              }}>2</Box>
              <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                Account lines
              </Typography>
              {lines.length > 0 && (
                <Chip size="small" variant="outlined"
                  label={`${lines.length} line${lines.length === 1 ? '' : 's'}`}
                  sx={{ height: 20, fontWeight: 700 }} />
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="contained"
                startIcon={<AddIcon fontSize="small" />} onClick={addLineRow}>
                Add line
              </Button>
            </Box>
            <Box sx={{ p: 2 }}>
              <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
                One line per account or sub-account. Leave <b>Sub-account</b> empty to discount the whole account, or pick a specific item (e.g. <b>Tuition</b> inside <b>school_fees</b>) to discount only that line.
              </Alert>
              {lines.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary',
                  border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                  <SchemesIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 0.5 }} />
                  <Typography variant="body2" fontWeight={700}>No lines yet</Typography>
                  <Typography variant="caption">Click "Add line" above to define your first discount</Typography>
                </Box>
              ) : (
                <Stack spacing={1.2}>
                  {lines.map((l, i) => {
                    const isPct = (l.mode || 'percentage') === 'percentage';
                    const subOptions = subAccountsByAccount[l.account_name] || [];
                    const isSub = !!(l.sub_account && l.sub_account.trim());
                    return (
                      <Paper key={i} variant="outlined" sx={{
                        p: 1.4, borderRadius: 1.5,
                        borderLeft: (t) => `3px solid ${
                          isSub ? t.palette.secondary.main : t.palette.primary.main}`,
                        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.02) },
                      }}>
                        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mb: 0.8 }}>
                          <Chip size="small"
                            label={`Line ${i + 1}`}
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                          <Chip size="small"
                            color={isSub ? 'secondary' : 'primary'} variant="outlined"
                            label={isSub ? 'Sub-account' : 'Whole account'}
                            sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                          <Box sx={{ flex: 1 }} />
                          <Tooltip title="Remove this line">
                            <IconButton size="small" color="error"
                              onClick={() => removeLineAt(i)}
                              sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.1) } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }}
                          spacing={1} alignItems={{ md: 'center' }}>
                          <Autocomplete size="small" sx={{ flex: 1.4 }}
                            options={individualAccounts}
                            value={l.account_name || null}
                            onChange={(_, v) => setLineAt(i, { account_name: v || '' })}
                            getOptionLabel={(o) => o || ''}
                            renderInput={(p) => (
                              <TextField {...p} label="Account" required placeholder="e.g. school_fees"
                                InputProps={{ ...p.InputProps,
                                  startAdornment: (
                                    <>
                                      <AccountIcon fontSize="small"
                                        sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                                      {p.InputProps.startAdornment}
                                    </>
                                  ) }} />
                            )} />
                          <Autocomplete size="small" sx={{ flex: 1.4 }} freeSolo
                            options={subOptions}
                            value={l.sub_account || ''}
                            disabled={!l.account_name}
                            onChange={(_, v) => setLineAt(i, { sub_account: v || '' })}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') setLineAt(i, { sub_account: v });
                            }}
                            getOptionLabel={(o) => o || ''}
                            renderInput={(p) => (
                              <TextField {...p}
                                label="Sub-account (optional)"
                                placeholder={!l.account_name
                                  ? 'Pick an account first'
                                  : (subOptions.length ? 'e.g. Tuition' : 'No sub-accounts - leave empty')} />
                            )} />
                          <ToggleButtonGroup size="small" exclusive
                            value={l.mode || 'percentage'}
                            onChange={(_, v) => { if (v) setLineAt(i, { mode: v }); }}
                            sx={{ '& .MuiToggleButton-root': {
                              px: 1.4, py: 0.5, fontWeight: 800, fontSize: 12,
                            } }}>
                            <ToggleButton value="percentage">%</ToggleButton>
                            <ToggleButton value="amount">GHS</ToggleButton>
                          </ToggleButtonGroup>
                          <TextField size="small" type="number" sx={{ width: 140 }}
                            label={isPct ? 'Percent off' : 'Amount off'}
                            value={isPct ? (l.percentage ?? '') : (l.amount ?? '')}
                            onChange={(e) => setLineAt(i,
                              isPct ? { percentage: e.target.value } : { amount: e.target.value })}
                            inputProps={{ min: 0,
                              ...(isPct ? { max: 100 } : {}),
                              step: '0.01',
                              style: { textAlign: 'right', ...TNUM } }}
                            InputProps={{ endAdornment: (
                              <InputAdornment position="end" sx={{ fontWeight: 700 }}>
                                {isPct ? '%' : 'GHS'}
                              </InputAdornment>
                            ) }} />
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.6, gap: 1, borderTop: 1, borderColor: 'divider' }}>
        {lines.length > 0 && (
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Summary:
            </Typography>
            <Chip size="small" variant="outlined" color="primary"
              label={`${wholeLineCount} whole-account`}
              sx={{ height: 22, fontWeight: 700 }} />
            <Chip size="small" variant="outlined" color="secondary"
              label={`${subLineCount} sub-account`}
              sx={{ height: 22, fontWeight: 700 }} />
          </Stack>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" size="large" onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ fontWeight: 800, minWidth: 180 }}>
          {saving ? 'Saving...' : (scheme ? 'Save changes' : 'Create scheme')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Enroll students dialog ────────────────────────────────────
function EnrollDialog({ open, onClose, onSaved, scheme, classes }) {
  const [classFilter, setClassFilter] = useState('');
  const [startDate, setStartDate]     = useState(todayISO());
  const [eligible, setEligible]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [picked, setPicked]           = useState({});
  const [saving, setSaving]           = useState(false);

  const loadEligible = async () => {
    if (!scheme?.id) return;
    setLoading(true);
    try {
      const params = { scheme_id: scheme.id };
      if (classFilter) params.cla = classFilter;
      const r = await API.get('/scholarships/eligible-students', { params });
      setEligible(r.data || []);
      const fresh = {};
      for (const s of r.data || []) fresh[s.index_no] = false;
      setPicked(fresh);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load eligible students')); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    setClassFilter('');
    setStartDate(todayISO());
    setEligible([]);
    setPicked({});
    // Defer one tick so the dialog is fully mounted before fetching.
    const t = setTimeout(loadEligible, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheme?.id]);

  useEffect(() => { if (open) loadEligible(); /* eslint-disable-next-line */ }, [classFilter]);

  const selectedCount = useMemo(() => Object.values(picked).filter(Boolean).length, [picked]);

  const togglePick = (sid) => setPicked(p => ({ ...p, [sid]: !p[sid] }));
  const pickAll  = () => {
    const next = {};
    for (const s of eligible) next[s.index_no] = true;
    setPicked(next);
  };
  const pickNone = () => {
    const next = {};
    for (const s of eligible) next[s.index_no] = false;
    setPicked(next);
  };

  const save = async () => {
    const students = eligible.filter(s => picked[s.index_no])
      .map(s => ({ student_id: s.index_no, student_name: s.name, class_name: s.class_name }));
    if (students.length === 0) return swal.error('Pick at least one student', 'Nothing to save');
    setSaving(true);
    try {
      const r = await API.post('/scholarships/enrollments/bulk', {
        scheme_id: scheme.id, start_date: startDate, students,
      });
      swal.success(r.data?.message || 'Enrolled');
      onSaved?.();
      onClose();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not enroll'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{
        py: 1.6, px: 2.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
        borderBottom: 1, borderColor: 'divider',
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
          color: 'primary.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <PersonIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.1 }} noWrap>
            Enroll Students
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            Scheme: <b>{scheme?.name}</b>
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5, bgcolor: 'grey.50' }}>
        <Stack spacing={2}>
          {/* Filters card */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 1.6, display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <Autocomplete size="small" options={classes}
                value={classFilter || null}
                onChange={(_, v) => setClassFilter(v || '')}
                renderInput={(p) => (
                  <TextField {...p} label="Class (filter)" placeholder="All classes"
                    InputProps={{ ...p.InputProps,
                      startAdornment: (
                        <>
                          <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                          {p.InputProps.startAdornment}
                        </>
                      ) }} />
                )} />
              <TextField size="small" type="date" label="Start date" required
                InputLabelProps={{ shrink: true }}
                value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Box>
          </Card>

          {/* Eligible students card */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexWrap: 'wrap' }}>
              <PersonIcon fontSize="small" color="primary" />
              <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                Eligible students
              </Typography>
              <Chip size="small" variant="outlined"
                label={loading ? '...' :
                  selectedCount > 0
                    ? `${eligible.length} eligible, ${selectedCount} selected`
                    : `${eligible.length} eligible`}
                color={selectedCount > 0 ? 'primary' : 'default'}
                sx={{ height: 22, fontWeight: 700 }} />
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={pickAll}  disabled={!eligible.length}>Select all</Button>
              <Button size="small" onClick={pickNone} disabled={!eligible.length}>Clear</Button>
              <Tooltip title="Reload eligible">
                <IconButton size="small" onClick={loadEligible}><RefreshIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Box>

            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50', fontSize: 11, fontWeight: 800,
                  textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 44 }} />
                    <TableCell>Student</TableCell>
                    <TableCell>Index</TableCell>
                    <TableCell>Class</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={22} /></TableCell></TableRow>
                  )}
                  {!loading && eligible.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center"
                      sx={{ py: 4, color: 'text.secondary' }}>
                      <PersonIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
                      <Typography variant="body2" fontWeight={700}>No eligible students</Typography>
                      <Typography variant="caption">
                        Everyone in {classFilter || 'this scope'} is already enrolled.
                      </Typography>
                    </TableCell></TableRow>
                  )}
                  {!loading && eligible.map(s => {
                    const sel = !!picked[s.index_no];
                    return (
                      <TableRow key={s.index_no} hover
                        onClick={() => togglePick(s.index_no)}
                        sx={{ cursor: 'pointer',
                          bgcolor: sel ? (t) => alpha(t.palette.primary.main, 0.08) : 'inherit',
                          borderLeft: (t) => sel ? `3px solid ${t.palette.primary.main}` : '3px solid transparent' }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Checkbox size="small" checked={sel}
                            icon={<CheckboxOffIcon fontSize="small" />}
                            checkedIcon={<CheckboxOnIcon fontSize="small" />} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: sel ? 800 : 700 }}>
                          <Stack direction="row" alignItems="center" spacing={0.7}>
                            <Box sx={{
                              width: 24, height: 24, borderRadius: '50%',
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
                              color: 'primary.main', display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              fontSize: 10, fontWeight: 800, flexShrink: 0,
                            }}>
                              {(s.name || s.index_no || '?').charAt(0).toUpperCase()}
                            </Box>
                            {s.name}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{s.index_no}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {s.class_name
                            ? <Chip size="small" label={s.class_name} variant="outlined"
                                sx={{ height: 20, fontWeight: 700 }} />
                            : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.6, gap: 1, borderTop: 1, borderColor: 'divider' }}>
        {selectedCount > 0 && (
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Will enroll
            </Typography>
            <Chip size="small" color="primary" variant="filled"
              label={`${selectedCount} student${selectedCount === 1 ? '' : 's'}`}
              sx={{ height: 22, fontWeight: 700 }} />
            <Typography variant="caption" color="text.secondary">
              starting <b>{startDate}</b>
            </Typography>
          </Stack>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" size="large" onClick={save}
          disabled={saving || selectedCount === 0}
          sx={{ fontWeight: 800, minWidth: 200 }}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
          {saving ? 'Saving...' : `Enroll ${selectedCount} student${selectedCount === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── One-time scholarship dialog (master-detail layout) ────────
function OneTimeDialog({ open, onClose, onSaved, classes }) {
  // Left pane — students
  const [students, setStudents]         = useState([]);
  const [loadingList, setLoadingList]   = useState(false);
  const [search, setSearch]             = useState('');
  const [classFilter, setClassFilter]   = useState('');

  // Right pane — selection + form
  const [student, setStudent]           = useState(null);
  const [accounts, setAccounts]         = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [account, setAccount]           = useState(null);
  const [direction, setDirection]       = useState('write_off'); // 'write_off' | 'increase'
  const [mode, setMode]                 = useState('amount'); // 'amount' | 'percentage'
  const [value, setValue]               = useState('');
  const [reason, setReason]             = useState('');
  const [saving, setSaving]             = useState(false);

  // Reset whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setSearch(''); setClassFilter('');
    setStudent(null); setAccounts([]); setAccount(null);
    setDirection('write_off'); setMode('amount'); setValue(''); setReason('');
  }, [open]);

  // Load students whenever filters change. Debounced search.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingList(true);
    const t = setTimeout(async () => {
      try {
        const params = { limit: 500 };
        if (search.trim())  params.q   = search.trim();
        if (classFilter)    params.cla = classFilter;
        const r = await API.get('/payments/students', { params });
        if (!cancelled) setStudents(r.data || []);
      } catch (e) {
        if (!cancelled) swal.error(swal.pickError(e, 'Could not load students'));
      } finally { if (!cancelled) setLoadingList(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search, classFilter]);

  // When a student is picked, load their fee accounts
  useEffect(() => {
    setAccount(null); setValue(''); setMode('amount'); setReason('');
    setDirection('write_off');
    if (!student?.index_no) { setAccounts([]); return; }
    setLoadingAccounts(true);
    (async () => {
      try {
        const r = await API.get(`/payments/students/${student.index_no}/fee-accounts`);
        setAccounts(r.data?.accounts || []);
      } catch (e) {
        swal.error(swal.pickError(e, 'Could not load student accounts'));
      } finally { setLoadingAccounts(false); }
    })();
  }, [student?.index_no]);

  // Live preview of computed amount + new balance
  const preview = useMemo(() => {
    if (!account) return null;
    const tobe    = Number(account.tobe || 0);
    const balance = Number(account.balance || 0);
    const v       = Number(value) || 0;
    if (v <= 0) return { amount: 0, newBalance: balance, error: null };
    const amount = mode === 'percentage' ? +(tobe * (v / 100)).toFixed(2) : +v.toFixed(2);
    if (mode === 'percentage' && v > 100) return { amount, newBalance: balance, error: 'Percentage cannot exceed 100' };
    // Write-off cannot exceed balance; increase has no upper bound.
    if (direction === 'write_off' && amount > balance + 0.005) {
      return { amount, newBalance: balance, error: 'Write-off exceeds remaining balance' };
    }
    const newBalance = direction === 'increase' ? balance + amount : balance - amount;
    return { amount, newBalance, error: null };
  }, [account, direction, mode, value]);

  const save = async () => {
    if (!student)         return swal.error('Pick a student', 'Missing student');
    if (!account)         return swal.error('Pick an account', 'Missing account');
    if (!value || Number(value) <= 0) return swal.error('Enter a value greater than zero', 'Missing value');
    if (preview?.error)   return swal.error(preview.error, 'Invalid');
    setSaving(true);
    try {
      const r = await API.post('/scholarships/one-time', {
        student_id:   student.index_no,
        account_name: account.account,
        direction, mode, value: Number(value), reason,
      });
      const verb = (r.data?.direction || direction) === 'increase' ? 'Added' : 'Wrote off';
      swal.success(
        `${verb} GHS ${(r.data?.amount || 0).toFixed(2)} - new balance GHS ${(r.data?.new_balance || 0).toFixed(2)}`
      );
      onSaved?.();
      onClose();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not apply adjustment'));
    } finally { setSaving(false); }
  };

  const fullName = (s) =>
    `${(s.firstname || '').trim()} ${(s.lastname || '').trim()}`.trim() || s.index_no;

  // Visual color flips with direction
  const dirColor = direction === 'increase' ? 'warning' : 'success';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: { xs: '100%', md: '85vh' }, borderRadius: 2 } }}>
      <DialogTitle sx={{
        py: 1.6, px: 2.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: (t) => alpha(t.palette[dirColor].main, 0.08),
        borderBottom: 1, borderColor: 'divider',
        transition: 'background-color 200ms',
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5,
          bgcolor: (t) => alpha(t.palette[dirColor].main, 0.18),
          color: `${dirColor}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 200ms, color 200ms',
        }}>
          <OneTimeIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.1 }}>
            Apply Adjustment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            One-shot {direction === 'increase' ? 'bill increase' : 'write-off'} against a single student account
          </Typography>
        </Box>
        <Chip size="small"
          label={direction === 'increase' ? 'Increase bill' : 'Write-off'}
          color={dirColor} variant="filled"
          sx={{ height: 24, fontWeight: 800 }} />
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '420px 1fr' },
          height: '100%',
        }}>
          {/* ── Left pane: students ─────────────────────────── */}
          <Box sx={{
            borderRight: { md: 1 }, borderColor: { md: 'divider' },
            display: 'flex', flexDirection: 'column', minHeight: 0,
          }}>
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider',
              display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.6}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={800}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Students
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Chip size="small" variant="outlined"
                  label={loadingList ? '...' : `${students.length}`}
                  sx={{ height: 18, fontWeight: 700 }} />
              </Stack>
              <TextField size="small" placeholder="Search name or index"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">
                  <SearchIcon fontSize="small" /></InputAdornment> }} />
              <Autocomplete size="small" options={classes || []}
                value={classFilter || null}
                onChange={(_, v) => setClassFilter(v || '')}
                renderInput={(p) => (
                  <TextField {...p} placeholder="All classes"
                    InputProps={{ ...p.InputProps,
                      startAdornment: (
                        <>
                          <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                          {p.InputProps.startAdornment}
                        </>
                      ) }} />
                )} />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {loadingList && (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
              )}
              {!loadingList && students.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="caption">No students match your filters</Typography>
                </Box>
              )}
              {!loadingList && students.map(s => {
                const sel = student?.index_no === s.index_no;
                return (
                  <Box key={s.index_no}
                    onClick={() => setStudent(s)}
                    sx={{
                      px: 1.5, py: 0.9, cursor: 'pointer',
                      borderBottom: 1, borderColor: 'divider',
                      bgcolor: (t) => sel ? alpha(t.palette.primary.main, 0.10) : 'transparent',
                      borderLeft: (t) => sel ? `3px solid ${t.palette.primary.main}` : '3px solid transparent',
                      '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
                    }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.18),
                        color: 'primary.main', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800, flexShrink: 0,
                      }}>
                        {(s.firstname || s.index_no || '?').charAt(0).toUpperCase()}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography fontSize={12.5} fontWeight={sel ? 800 : 700} noWrap>
                          {fullName(s)}
                        </Typography>
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {s.index_no}
                          </Typography>
                          {s.calss && (
                            <Chip size="small" label={s.calss} variant="outlined"
                              sx={{ height: 16, fontSize: 9.5, fontWeight: 700, '.MuiChip-label': { px: 0.6 } }} />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* ── Right pane: actions ─────────────────────────── */}
          <Box sx={{ p: 2.5, overflowY: 'auto', minHeight: 0, bgcolor: 'grey.50' }}>
            {!student ? (
              <Box sx={{ py: 10, textAlign: 'center', color: 'text.secondary' }}>
                <Box sx={{
                  width: 80, height: 80, borderRadius: '50%', mx: 'auto', mb: 2,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                  color: 'primary.main',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <PersonIcon sx={{ fontSize: 44 }} />
                </Box>
                <Typography fontWeight={800}>Select a student to begin</Typography>
                <Typography variant="caption">Pick from the list on the left</Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {/* Selected student card */}
                <Card variant="outlined" sx={{ borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette[dirColor].main, 0.04),
                  borderLeft: (t) => `3px solid ${t.palette[dirColor].main}`,
                  transition: 'background-color 200ms, border-color 200ms' }}>
                  <CardContent sx={{ py: 1.4, '&:last-child': { pb: 1.4 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.4}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '50%',
                        bgcolor: (t) => alpha(t.palette[dirColor].main, 0.22),
                        color: `${dirColor}.main`, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, fontWeight: 800,
                      }}>
                        {(student.firstname || student.index_no).charAt(0).toUpperCase()}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={800}>{fullName(student)}</Typography>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <Typography variant="caption" color="text.secondary">{student.index_no}</Typography>
                          {student.calss && (
                            <Chip size="small" label={student.calss} variant="outlined"
                              sx={{ height: 18, fontWeight: 700 }} />
                          )}
                          {student.type && (
                            <Chip size="small" label={student.type} color="secondary" variant="outlined"
                              sx={{ height: 18, fontWeight: 700 }} />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Step 1 — Account picker */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ px: 1.6, py: 1, borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%',
                      bgcolor: 'primary.main', color: 'primary.contrastText',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                    }}>1</Box>
                    <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                      Account
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.6 }}>
                    <Autocomplete size="small" options={accounts}
                      value={account} disabled={loadingAccounts}
                      onChange={(_, v) => setAccount(v)}
                      getOptionLabel={(o) => o?.account || ''}
                      isOptionEqualToValue={(a, b) => a.account === b.account}
                      renderOption={(props, o) => (
                        <li {...props} key={o.account}>
                          <Box sx={{ width: '100%' }}>
                            <Stack direction="row" spacing={1} alignItems="baseline">
                              <Typography fontSize={13} fontWeight={700}>{o.account}</Typography>
                              <Box sx={{ flex: 1 }} />
                              <Typography variant="caption" color="text.secondary" sx={TNUM}>
                                Balance: GHS {Number(o.balance || 0).toFixed(2)}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.disabled">
                              Bill: GHS {Number(o.tobe || 0).toFixed(2)} | Paid: GHS {Number(o.t_paid || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(p) => (
                        <TextField {...p}
                          placeholder={loadingAccounts ? 'Loading account list...' : 'Pick an account'}
                          InputProps={{ ...p.InputProps,
                            startAdornment: (
                              <>
                                <AccountIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                                {p.InputProps.startAdornment}
                              </>
                            ) }} />
                      )} />
                    {account && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                        <Chip size="small" variant="outlined"
                          label={`Bill: GHS ${Number(account.tobe || 0).toFixed(2)}`}
                          sx={{ fontWeight: 700 }} />
                        <Chip size="small" variant="outlined" color="success"
                          label={`Paid: GHS ${Number(account.t_paid || 0).toFixed(2)}`}
                          sx={{ fontWeight: 700 }} />
                        <Chip size="small" variant="outlined" color="warning"
                          label={`Balance: GHS ${Number(account.balance || 0).toFixed(2)}`}
                          sx={{ fontWeight: 700 }} />
                      </Stack>
                    )}
                  </Box>
                </Card>

                {/* Step 2 — Direction */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ px: 1.6, py: 1, borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%',
                      bgcolor: 'primary.main', color: 'primary.contrastText',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                    }}>2</Box>
                    <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                      Direction
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.6 }}>
                    <ToggleButtonGroup size="small" exclusive fullWidth
                      value={direction} onChange={(_, v) => { if (v) setDirection(v); }}
                      sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, py: 0.8 } }}>
                      <ToggleButton value="write_off"
                        sx={{
                          '&.Mui-selected': {
                            color: 'success.main',
                            bgcolor: (t) => alpha(t.palette.success.main, 0.14),
                            borderColor: 'success.main',
                          },
                        }}>
                        Write-off (decrease bill)
                      </ToggleButton>
                      <ToggleButton value="increase"
                        sx={{
                          '&.Mui-selected': {
                            color: 'warning.main',
                            bgcolor: (t) => alpha(t.palette.warning.main, 0.16),
                            borderColor: 'warning.main',
                          },
                        }}>
                        Increase (add to bill)
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Card>

                {/* Step 3 — Amount */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ px: 1.6, py: 1, borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%',
                      bgcolor: 'primary.main', color: 'primary.contrastText',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                    }}>3</Box>
                    <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                      {direction === 'increase' ? 'Increase amount' : 'Discount'}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.6 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <ToggleButtonGroup size="small" exclusive
                        value={mode} onChange={(_, v) => { if (v) setMode(v); }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.4 } }}>
                        <ToggleButton value="amount">Amount</ToggleButton>
                        <ToggleButton value="percentage">Percentage</ToggleButton>
                      </ToggleButtonGroup>
                      <TextField size="small" type="number" sx={{ flex: 1 }}
                        placeholder={mode === 'percentage' ? 'e.g. 25' : 'e.g. 150.00'}
                        value={value} onChange={(e) => setValue(e.target.value)}
                        inputProps={{ min: 0, step: '0.01',
                          style: { textAlign: 'right', ...TNUM } }}
                        InputProps={{ endAdornment: (
                          <InputAdornment position="end" sx={{ fontWeight: 700 }}>
                            {mode === 'percentage' ? '%' : 'GHS'}
                          </InputAdornment>
                        ) }} />
                    </Stack>

                    {/* Live preview */}
                    {account && (
                      <Alert severity={preview?.error ? 'error' : dirColor}
                        variant="outlined" sx={{ borderRadius: 1.5, mt: 1.5 }}>
                        {preview?.error ? (
                          preview.error
                        ) : (
                          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2">
                              <b>{direction === 'increase' ? 'Will add to bill:' : 'Will write off:'}</b>{' '}
                              <span style={TNUM}>GHS {(preview?.amount || 0).toFixed(2)}</span>
                            </Typography>
                            <Typography variant="body2">
                              <b>New balance:</b>{' '}
                              <span style={TNUM}>GHS {(preview?.newBalance ?? 0).toFixed(2)}</span>
                            </Typography>
                          </Stack>
                        )}
                      </Alert>
                    )}
                  </Box>
                </Card>

                {/* Step 4 — Reason */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ px: 1.6, py: 1, borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%',
                      bgcolor: 'primary.main', color: 'primary.contrastText',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                    }}>4</Box>
                    <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                      Reason (optional)
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.6 }}>
                    <TextField size="small" fullWidth value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      multiline minRows={2}
                      placeholder="e.g. Termly bursary, hardship case..." />
                  </Box>
                </Card>
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.6, gap: 1, borderTop: 1, borderColor: 'divider' }}>
        {student && account && preview && !preview.error && Number(preview.amount) > 0 && (
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Summary:
            </Typography>
            <Chip size="small" color={dirColor} variant="filled"
              label={`${direction === 'increase' ? '+' : '-'}GHS ${preview.amount.toFixed(2)}`}
              sx={{ height: 22, fontWeight: 800 }} />
            <Typography variant="caption" color="text.secondary">
              on <b>{account.account}</b>, new balance{' '}
              <span style={{ ...TNUM, fontWeight: 700 }}>
                GHS {Number(preview.newBalance).toFixed(2)}
              </span>
            </Typography>
          </Stack>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" color={dirColor} size="large" onClick={save}
          disabled={saving || !student || !account || !value || !!preview?.error}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ fontWeight: 800, minWidth: 220 }}>
          {saving ? 'Applying...' : (direction === 'increase' ? 'Apply increase' : 'Apply write-off')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Remove fee account from a student (master-detail) ────────
function RemoveAccountDialog({ open, onClose, onSaved, classes }) {
  const [students, setStudents]         = useState([]);
  const [loadingList, setLoadingList]   = useState(false);
  const [search, setSearch]             = useState('');
  const [classFilter, setClassFilter]   = useState('');

  const [student, setStudent]           = useState(null);
  const [accounts, setAccounts]         = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [account, setAccount]           = useState(null);
  const [reason, setReason]             = useState('');
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch(''); setClassFilter('');
    setStudent(null); setAccounts([]); setAccount(null); setReason('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingList(true);
    const t = setTimeout(async () => {
      try {
        const params = { limit: 500 };
        if (search.trim())  params.q   = search.trim();
        if (classFilter)    params.cla = classFilter;
        const r = await API.get('/payments/students', { params });
        if (!cancelled) setStudents(r.data || []);
      } catch (e) {
        if (!cancelled) swal.error(swal.pickError(e, 'Could not load students'));
      } finally { if (!cancelled) setLoadingList(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search, classFilter]);

  useEffect(() => {
    setAccount(null); setReason('');
    if (!student?.index_no) { setAccounts([]); return; }
    setLoadingAccounts(true);
    (async () => {
      try {
        const r = await API.get(`/payments/students/${student.index_no}/fee-accounts`);
        setAccounts(r.data?.accounts || []);
      } catch (e) {
        swal.error(swal.pickError(e, 'Could not load student accounts'));
      } finally { setLoadingAccounts(false); }
    })();
  }, [student?.index_no]);

  const fullName = (s) =>
    `${(s.firstname || '').trim()} ${(s.lastname || '').trim()}`.trim() || s.index_no;

  // Block when there are payments — the backend will reject anyway,
  // but failing fast in the UI is friendlier.
  const paidGuard = useMemo(() => {
    if (!account) return null;
    const paid = Number(account.t_paid || 0);
    if (paid > 0) return `Student has already paid GHS ${paid.toFixed(2)} into this account. Reverse those receipts first.`;
    return null;
  }, [account]);

  const save = async () => {
    if (!student) return swal.error('Pick a student', 'Missing student');
    if (!account) return swal.error('Pick an account', 'Missing account');
    if (!reason || reason.trim().length < 5) {
      return swal.error('Give a reason of at least 5 characters', 'Missing reason');
    }
    if (paidGuard) return swal.error(paidGuard, 'Has payments');

    const ok = await swal.confirm({
      title: `Remove "${account.account}" from ${fullName(student)}?`,
      text: 'This deletes the bill rows, the dynamic fee row and the payment_detail line. Cannot be undone from the UI.',
      tone: 'danger',
      icon: 'warning',
      confirmText: 'Yes, remove the account',
    });
    if (!ok) return;

    setSaving(true);
    try {
      const r = await API.post('/scholarships/remove-fee-account', {
        student_id:   student.index_no,
        account_name: account.account,
        reason,
      });
      const tail = r.data?.child_state_reset_to_unassigned
        ? ' Student is now back in the unassigned picker.'
        : '';
      swal.success(`Removed "${account.account}" from ${fullName(student)}.${tail}`);
      onSaved?.();
      onClose();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not remove account'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { height: { xs: '100%', md: '85vh' }, borderRadius: 2 } }}>
      <DialogTitle sx={{
        py: 1.6, px: 2.5, fontWeight: 800,
        display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: (t) => alpha(t.palette.error.main, 0.08),
        borderBottom: 1, borderColor: 'divider',
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5,
          bgcolor: (t) => alpha(t.palette.error.main, 0.18),
          color: 'error.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <RemoveAccountIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.1 }}>
            Remove Fee Account
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cleanly delete a wrongly-assigned account from a student. Refused if any payment has posted.
          </Typography>
        </Box>
        <Chip size="small" label="Destructive" color="error" variant="filled"
          sx={{ height: 24, fontWeight: 800 }} />
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '420px 1fr' },
          height: '100%',
        }}>
          {/* Left: students */}
          <Box sx={{
            borderRight: { md: 1 }, borderColor: { md: 'divider' },
            display: 'flex', flexDirection: 'column', minHeight: 0,
          }}>
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider',
              display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.6}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="caption" fontWeight={800}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Students
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Chip size="small" variant="outlined"
                  label={loadingList ? '...' : `${students.length}`}
                  sx={{ height: 18, fontWeight: 700 }} />
              </Stack>
              <TextField size="small" placeholder="Search name or index"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">
                  <SearchIcon fontSize="small" /></InputAdornment> }} />
              <Autocomplete size="small" options={classes || []}
                value={classFilter || null}
                onChange={(_, v) => setClassFilter(v || '')}
                renderInput={(p) => (
                  <TextField {...p} placeholder="All classes"
                    InputProps={{ ...p.InputProps,
                      startAdornment: (
                        <>
                          <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                          {p.InputProps.startAdornment}
                        </>
                      ) }} />
                )} />
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {loadingList && (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
              )}
              {!loadingList && students.length === 0 && (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="caption">No students match your filters</Typography>
                </Box>
              )}
              {!loadingList && students.map(s => {
                const sel = student?.index_no === s.index_no;
                return (
                  <Box key={s.index_no}
                    onClick={() => setStudent(s)}
                    sx={{
                      px: 1.5, py: 0.9, cursor: 'pointer',
                      borderBottom: 1, borderColor: 'divider',
                      bgcolor: (t) => sel ? alpha(t.palette.error.main, 0.08) : 'transparent',
                      borderLeft: (t) => sel ? `3px solid ${t.palette.error.main}` : '3px solid transparent',
                      '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.04) },
                    }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        bgcolor: (t) => alpha(t.palette.error.main, 0.18),
                        color: 'error.main', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800, flexShrink: 0,
                      }}>
                        {(s.firstname || s.index_no || '?').charAt(0).toUpperCase()}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography fontSize={12.5} fontWeight={sel ? 800 : 700} noWrap>
                          {fullName(s)}
                        </Typography>
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <Typography variant="caption" color="text.secondary">{s.index_no}</Typography>
                          {s.calss && (
                            <Chip size="small" label={s.calss} variant="outlined"
                              sx={{ height: 16, fontSize: 9.5, fontWeight: 700, '.MuiChip-label': { px: 0.6 } }} />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Right: action */}
          <Box sx={{ p: 2, overflowY: 'auto', minHeight: 0 }}>
            {!student ? (
              <Box sx={{ py: 10, textAlign: 'center', color: 'text.secondary' }}>
                <Box sx={{
                  width: 80, height: 80, borderRadius: '50%', mx: 'auto', mb: 2,
                  bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                  color: 'error.main',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <RemoveAccountIcon sx={{ fontSize: 44 }} />
                </Box>
                <Typography fontWeight={800}>Select a student to begin</Typography>
                <Typography variant="caption">Pick from the list on the left</Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Card variant="outlined" sx={{ borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.error.main, 0.04),
                  borderLeft: (t) => `3px solid ${t.palette.error.main}` }}>
                  <CardContent sx={{ py: 1.4, '&:last-child': { pb: 1.4 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.4}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '50%',
                        bgcolor: (t) => alpha(t.palette.error.main, 0.22),
                        color: 'error.main', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, fontWeight: 800,
                      }}>
                        {(student.firstname || student.index_no).charAt(0).toUpperCase()}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={800}>{fullName(student)}</Typography>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <Typography variant="caption" color="text.secondary">{student.index_no}</Typography>
                          {student.calss && (
                            <Chip size="small" label={student.calss} variant="outlined"
                              sx={{ height: 18, fontWeight: 700 }} />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Step 1 — Account picker */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ px: 1.6, py: 1, borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%',
                      bgcolor: 'error.main', color: 'error.contrastText',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                    }}>1</Box>
                    <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                      Account to remove
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.6 }}>
                    <Autocomplete size="small" options={accounts}
                      value={account} disabled={loadingAccounts}
                      onChange={(_, v) => setAccount(v)}
                      getOptionLabel={(o) => o?.account || ''}
                      isOptionEqualToValue={(a, b) => a.account === b.account}
                      renderOption={(props, o) => (
                        <li {...props} key={o.account}>
                          <Box sx={{ width: '100%' }}>
                            <Stack direction="row" spacing={1} alignItems="baseline">
                              <Typography fontSize={13} fontWeight={700}>{o.account}</Typography>
                              <Box sx={{ flex: 1 }} />
                              <Typography variant="caption" color="text.secondary" sx={TNUM}>
                                Paid: GHS {Number(o.t_paid || 0).toFixed(2)}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.disabled">
                              Bill: GHS {Number(o.tobe || 0).toFixed(2)} | Balance: GHS {Number(o.balance || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(p) => (
                        <TextField {...p}
                          placeholder={loadingAccounts ? 'Loading account list...' : 'Pick the account to remove'}
                          InputProps={{ ...p.InputProps,
                            startAdornment: (
                              <>
                                <AccountIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                                {p.InputProps.startAdornment}
                              </>
                            ) }} />
                      )} />
                    {account && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                        <Chip size="small" variant="outlined"
                          label={`Bill: GHS ${Number(account.tobe || 0).toFixed(2)}`}
                          sx={{ fontWeight: 700 }} />
                        <Chip size="small" variant="outlined" color="success"
                          label={`Paid: GHS ${Number(account.t_paid || 0).toFixed(2)}`}
                          sx={{ fontWeight: 700 }} />
                        <Chip size="small" variant="outlined" color="warning"
                          label={`Balance: GHS ${Number(account.balance || 0).toFixed(2)}`}
                          sx={{ fontWeight: 700 }} />
                      </Stack>
                    )}
                  </Box>
                </Card>

                {/* Risk banner */}
                {paidGuard ? (
                  <Alert severity="error" variant="filled" icon={<WarningIcon />}
                    sx={{ borderRadius: 1.5 }}>
                    <Typography fontWeight={800}>Cannot remove</Typography>
                    <Typography variant="body2">{paidGuard}</Typography>
                  </Alert>
                ) : account && (
                  <Alert severity="warning" variant="outlined" icon={<WarningIcon />}
                    sx={{ borderRadius: 1.5 }}>
                    <Typography fontWeight={800}>This action is permanent</Typography>
                    <Typography variant="body2">
                      Deletes the admission lines, the dynamic fee row and the payment_detail row for <b>{account.account}</b>. If this is the student's only account, they will return to the unassigned picker.
                    </Typography>
                  </Alert>
                )}

                {/* Step 2 — Reason */}
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <Box sx={{ px: 1.6, py: 1, borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%',
                      bgcolor: 'error.main', color: 'error.contrastText',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                    }}>2</Box>
                    <Typography variant="overline" fontWeight={800} sx={{ letterSpacing: 1 }}>
                      Reason (required, min 5 characters)
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.6 }}>
                    <TextField size="small" fullWidth value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      multiline minRows={2}
                      placeholder="e.g. Account was assigned in error, this student doesn't take tutorial..." />
                  </Box>
                </Card>
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.6, gap: 1, borderTop: 1, borderColor: 'divider' }}>
        {student && account && !paidGuard && (
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              About to remove:
            </Typography>
            <Chip size="small" color="error" variant="outlined"
              label={account.account}
              sx={{ height: 22, fontWeight: 800 }} />
            <Typography variant="caption" color="text.secondary">
              from <b>{fullName(student)}</b>
            </Typography>
          </Stack>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="error" size="large" onClick={save}
          disabled={saving || !student || !account || !!paidGuard || reason.trim().length < 5}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <RemoveAccountIcon />}
          sx={{ fontWeight: 800, minWidth: 220 }}>
          {saving ? 'Removing...' : 'Remove account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page ──────────────────────────────────────────────────────
export default function ScholarshipsPage() {
  const [tab, setTab] = useState(0);

  // Reference data
  const [accountsAll, setAccountsAll] = useState([]);
  const [classes, setClasses]         = useState([]);

  // Schemes tab
  const [schemes, setSchemes]         = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(true);
  const [editScheme, setEditScheme]   = useState(null);   // null = new; object = edit
  const [editOpen, setEditOpen]       = useState(false);
  const [enrollScheme, setEnrollScheme] = useState(null);
  const [enrollOpen, setEnrollOpen]   = useState(false);

  // Per-scheme expansion state. Each entry holds the enrolled students
  // for that scheme plus a search string used to filter them inline.
  const [expanded, setExpanded] = useState({}); // { [schemeId]: bool }
  const [schemeEnrol, setSchemeEnrol] = useState({});
  // shape: { [schemeId]: { loading, rows, search } }

  // One-time tab
  const [oneTimes, setOneTimes]       = useState([]);
  const [loadingOneTime, setLoadingOneTime] = useState(true);
  const [oneFiltQuery, setOneFiltQuery] = useState('');
  const [oneTimeOpen, setOneTimeOpen] = useState(false);
  const [removeOpen, setRemoveOpen]   = useState(false);

  const individualAccounts = useMemo(
    () => accountsAll.filter(a => a.category === 'Individual Payment').map(a => a.account),
    [accountsAll]
  );
  const classOptions = useMemo(() => classes.map(c => c.class).filter(Boolean), [classes]);

  const loadRefs = async () => {
    try {
      const [a, c] = await Promise.all([
        API.get('/account-setup'),
        API.get('/classes'),
      ]);
      setAccountsAll(a.data || []);
      setClasses(c.data || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load reference data')); }
  };

  const loadSchemes = async () => {
    setLoadingSchemes(true);
    try {
      const r = await API.get('/scholarships/schemes');
      setSchemes(r.data || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load schemes')); }
    finally { setLoadingSchemes(false); }
  };

  const loadSchemeEnrollments = async (schemeId) => {
    setSchemeEnrol(prev => ({
      ...prev,
      [schemeId]: { ...(prev[schemeId] || { search: '' }), loading: true },
    }));
    try {
      const r = await API.get('/scholarships/enrollments', {
        params: { scheme_id: schemeId, active: 1 },
      });
      setSchemeEnrol(prev => ({
        ...prev,
        [schemeId]: {
          loading: false,
          rows:    r.data || [],
          search:  prev[schemeId]?.search || '',
        },
      }));
    } catch (e) {
      setSchemeEnrol(prev => ({
        ...prev,
        [schemeId]: { ...(prev[schemeId] || { search: '' }), loading: false, rows: [] },
      }));
      swal.error(swal.pickError(e, 'Could not load students for this scheme'));
    }
  };

  const toggleSchemeExpand = (scheme) => {
    setExpanded(prev => {
      const isOpen = !!prev[scheme.id];
      const next = { ...prev, [scheme.id]: !isOpen };
      if (!isOpen) loadSchemeEnrollments(scheme.id);
      return next;
    });
  };

  const setSchemeSearch = (schemeId, value) => {
    setSchemeEnrol(prev => ({
      ...prev,
      [schemeId]: { ...(prev[schemeId] || { rows: [], loading: false }), search: value },
    }));
  };

  const loadOneTimes = async () => {
    setLoadingOneTime(true);
    try {
      const params = {};
      if (oneFiltQuery) params.q = oneFiltQuery;
      const r = await API.get('/scholarships/one-time', { params });
      setOneTimes(r.data || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load adjustments')); }
    finally { setLoadingOneTime(false); }
  };

  useEffect(() => { loadRefs(); loadSchemes(); loadOneTimes(); }, []);
  useEffect(() => { loadOneTimes(); /* eslint-disable-next-line */ }, [oneFiltQuery]);

  // ── Scheme actions ────────────────────────────────────────
  const openNewScheme = () => { setEditScheme(null); setEditOpen(true); };

  const openEditScheme = async (sch) => {
    try {
      const r = await API.get(`/scholarships/schemes/${sch.id}`);
      setEditScheme(r.data);
      setEditOpen(true);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load scheme')); }
  };

  const removeScheme = async (sch) => {
    const ok = await swal.confirmDelete(`scheme "${sch.name}"`);
    if (!ok) return;
    try {
      const r = await API.delete(`/scholarships/schemes/${sch.id}`);
      swal.success(r.data?.message || 'Removed');
      loadSchemes();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  const openEnrollFor = (sch) => { setEnrollScheme(sch); setEnrollOpen(true); };

  const reapply = async () => {
    const ok = await swal.confirm({
      title: 'Reapply scholarships now?',
      text: 'Sets schola % on dynamic-table rows that currently have 0, for every active enrollment. Existing non-zero percentages are left alone.',
      tone: 'warning',
      confirmText: 'Yes, reapply',
    });
    if (!ok) return;
    try {
      const r = await API.post('/scholarships/reapply');
      swal.success(`Reapply complete - ${r.data?.rows_updated || 0} row(s) updated, ${r.data?.rows_kept_existing || 0} kept`);
    } catch (e) { swal.error(swal.pickError(e, 'Reapply failed')); }
  };

  // ── Enrollment actions ────────────────────────────────────
  const toggleEnrolment = async (row) => {
    try {
      await API.put(`/scholarships/enrollments/${row.id}`, { active: row.active ? 0 : 1 });
      loadSchemeEnrollments(row.scheme_id);
      loadSchemes();
    } catch (e) { swal.error(swal.pickError(e, 'Update failed')); }
  };

  const removeEnrolment = async (row) => {
    const ok = await swal.confirm({
      title: `Remove ${row.student_name || row.student_id} from ${row.scheme_name}?`,
      text: "No retroactive change to existing bills. Future bills won't apply this scheme.",
      tone: 'warning',
      confirmText: 'Yes, remove',
    });
    if (!ok) return;
    try {
      await API.delete(`/scholarships/enrollments/${row.id}`);
      loadSchemeEnrollments(row.scheme_id);
      loadSchemes();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  // ── One-time actions ──────────────────────────────────────
  const reverseOneTime = async (row) => {
    const ok = await swal.confirm({
      title: 'Reverse this adjustment?',
      text: `This subtracts GHS ${Number(row.schol_amount || 0).toFixed(2)} from the write-off so the bill is back to what it was before. Recorded in audit.`,
      tone: 'warning',
      confirmText: 'Yes, reverse',
    });
    if (!ok) return;
    try {
      await API.delete(`/scholarships/one-time/${row.id}`);
      swal.success('Reversed');
      loadOneTimes();
    } catch (e) { swal.error(swal.pickError(e, 'Reverse failed')); }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', fontFamily: FONT_ACCT }}>
      <ModuleHeader
        icon={<SchemesIcon />}
        title="Scholarship / Adjustment"
        subtitle="Define a scholarship template, then enroll students - percentages apply to future bills automatically"
      />

      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 44 } }}>
          <Tab label={`Schemes (${schemes.length})`} />
          <Tab label={`Adjustment / Written-off (${oneTimes.length})`} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Box sx={{
              px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexWrap: 'wrap',
            }}>
              <SchemesIcon color="primary" />
              <Typography variant="subtitle2" fontWeight={800}>Catalog</Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<ReapplyIcon fontSize="small" />} onClick={reapply}>
                Reapply now
              </Button>
              <Button size="small" variant="contained" startIcon={<AddIcon fontSize="small" />}
                onClick={openNewScheme}>
                New scheme
              </Button>
            </Box>

            <CardContent>
              {loadingSchemes ? (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
              ) : schemes.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                  <SchemesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography fontWeight={700}>No schemes yet</Typography>
                  <Typography variant="caption" color="text.disabled">
                    Click "New scheme" to define your first one.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <Table size="small" sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: 'grey.50', fontSize: 11, fontWeight: 800,
                      textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
                    },
                    '& tbody .MuiTableRow-root:hover': {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                    },
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell>Name</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center" sx={{ width: 100 }}>Lines</TableCell>
                        <TableCell align="center" sx={{ width: 110 }}>Enrolled</TableCell>
                        <TableCell align="center" sx={{ width: 80 }}>Active</TableCell>
                        <TableCell align="right" sx={{ width: 200 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {schemes.map(s => {
                        const isOpen = !!expanded[s.id];
                        const cache  = schemeEnrol[s.id] || { rows: [], loading: false, search: '' };
                        const term   = (cache.search || '').trim().toLowerCase();
                        const filtered = term
                          ? (cache.rows || []).filter(r =>
                              (r.student_name || '').toLowerCase().includes(term)
                              || (r.student_id   || '').toLowerCase().includes(term)
                              || (r.class_name   || '').toLowerCase().includes(term))
                          : (cache.rows || []);
                        return (
                          <Fragment key={s.id}>
                            <TableRow sx={{ '& td': { fontSize: 12, py: 0.7, borderBottom: isOpen ? 0 : undefined },
                              opacity: s.active ? 1 : 0.6,
                              bgcolor: isOpen ? (t) => alpha(t.palette.primary.main, 0.04) : undefined }}>
                              <TableCell sx={{ pl: 1 }}>
                                <IconButton size="small"
                                  onClick={() => toggleSchemeExpand(s)}
                                  sx={{
                                    transition: 'transform 200ms',
                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                  }}>
                                  <ExpandIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800, cursor: 'pointer' }}
                                onClick={() => toggleSchemeExpand(s)}>
                                {s.name}
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>{s.description || '-'}</TableCell>
                              <TableCell align="center">
                                <Chip size="small" variant="outlined"
                                  label={s.line_count} sx={{ fontWeight: 700, height: 20 }} />
                              </TableCell>
                              <TableCell align="center">
                                <Chip size="small" color={s.enrolled_count > 0 ? 'primary' : 'default'}
                                  variant="outlined"
                                  label={s.enrolled_count} sx={{ fontWeight: 700, height: 20 }} />
                              </TableCell>
                              <TableCell align="center">
                                <Chip size="small" color={s.active ? 'success' : 'default'}
                                  label={s.active ? 'Active' : 'Inactive'}
                                  sx={{ fontWeight: 700, height: 20 }} />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Enroll students">
                                  <span>
                                    <IconButton size="small" color="primary"
                                      disabled={!s.active}
                                      onClick={() => openEnrollFor(s)}>
                                      <PersonIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => openEditScheme(s)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" color="error" onClick={() => removeScheme(s)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={7} sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1.5, px: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                      <PersonIcon fontSize="small" color="primary" />
                                      <Typography variant="caption" fontWeight={800}
                                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Students enrolled
                                      </Typography>
                                      <Chip size="small" variant="outlined"
                                        label={cache.loading ? '...' :
                                          (filtered.length === (cache.rows || []).length
                                            ? `${filtered.length}`
                                            : `${filtered.length} of ${(cache.rows || []).length}`)}
                                        sx={{ fontWeight: 700, height: 20 }} />
                                      <Box sx={{ flex: 1 }} />
                                      <TextField size="small" placeholder="Search name, index, class"
                                        value={cache.search || ''}
                                        onChange={(e) => setSchemeSearch(s.id, e.target.value)}
                                        InputProps={{
                                          startAdornment: <InputAdornment position="start">
                                            <SearchIcon fontSize="small" /></InputAdornment>,
                                        }}
                                        sx={{ width: 280 }} />
                                      <Tooltip title="Reload">
                                        <IconButton size="small"
                                          onClick={() => loadSchemeEnrollments(s.id)}>
                                          <RefreshIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>

                                    {cache.loading ? (
                                      <Box sx={{ py: 3, textAlign: 'center' }}>
                                        <CircularProgress size={22} />
                                      </Box>
                                    ) : (cache.rows || []).length === 0 ? (
                                      <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography variant="caption">
                                          No students are enrolled in this scheme yet.
                                        </Typography>
                                      </Box>
                                    ) : filtered.length === 0 ? (
                                      <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography variant="caption">
                                          No matches for "{cache.search}"
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <TableContainer component={Paper} variant="outlined"
                                        sx={{ borderRadius: 1.5, maxHeight: 360 }}>
                                        <Table size="small" stickyHeader sx={{
                                          '& .MuiTableCell-head': {
                                            bgcolor: 'grey.50', fontSize: 10.5, fontWeight: 800,
                                            textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary',
                                          },
                                        }}>
                                          <TableHead>
                                            <TableRow>
                                              <TableCell>Student</TableCell>
                                              <TableCell>Index</TableCell>
                                              <TableCell>Class</TableCell>
                                              <TableCell sx={{ width: 110 }}>Start</TableCell>
                                              <TableCell sx={{ width: 110 }}>End</TableCell>
                                              <TableCell align="center" sx={{ width: 70 }}>Active</TableCell>
                                              <TableCell align="right" sx={{ width: 50 }} />
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {filtered.map(r => (
                                              <TableRow key={r.id}
                                                sx={{ '& td': { fontSize: 12, py: 0.6 },
                                                opacity: r.active ? 1 : 0.55 }}>
                                                <TableCell sx={{ fontWeight: 700 }}>{r.student_name || '-'}</TableCell>
                                                <TableCell>{r.student_id}</TableCell>
                                                <TableCell>
                                                  {r.class_name
                                                    ? <Chip size="small" label={r.class_name} variant="outlined"
                                                        sx={{ height: 18, fontWeight: 700 }} />
                                                    : '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                                                  {r.start_date ? String(r.start_date).slice(0, 10) : '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                                                  {r.end_date ? String(r.end_date).slice(0, 10) : '-'}
                                                </TableCell>
                                                <TableCell align="center">
                                                  <Switch size="small" checked={!!r.active}
                                                    onChange={() => toggleEnrolment(r)} />
                                                </TableCell>
                                                <TableCell align="right">
                                                  <Tooltip title="Remove enrollment">
                                                    <IconButton size="small" color="error"
                                                      onClick={() => removeEnrolment(r)}>
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{
              px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexWrap: 'wrap',
            }}>
              <OneTimeIcon color="primary" />
              <Typography variant="subtitle2" fontWeight={800}>Adjustments / Written-off</Typography>
              <Box sx={{ flex: 1 }} />
              <TextField size="small" placeholder="Search name / index"
                value={oneFiltQuery} onChange={(e) => setOneFiltQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                sx={{ width: 240 }} />
              <Tooltip title="Reload">
                <IconButton size="small" onClick={loadOneTimes}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" variant="outlined" color="error"
                startIcon={<RemoveAccountIcon fontSize="small" />}
                onClick={() => setRemoveOpen(true)}>
                Remove fee account
              </Button>
              <Button size="small" variant="contained" startIcon={<AddIcon fontSize="small" />}
                onClick={() => setOneTimeOpen(true)}>
                Apply adjustment
              </Button>
            </Box>

            <CardContent>
              <Alert severity="info" variant="outlined" sx={{ mb: 1.5, py: 0.4 }}>
                An adjustment is a single write-off against one (student, account). It bumps the account's
                <code>&nbsp;wri&nbsp;</code> column so the next balance lookup reflects it. Apply it once - it does
                not repeat on future bills.
              </Alert>
              {loadingOneTime ? (
                <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
              ) : oneTimes.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                  <OneTimeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography fontWeight={700}>No adjustments yet</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                  <Table size="small" sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: 'grey.50', fontSize: 11, fontWeight: 800,
                      textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
                    },
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Index</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Account</TableCell>
                        <TableCell sx={{ width: 110 }}>Direction</TableCell>
                        <TableCell align="right" sx={{ width: 90 }}>%</TableCell>
                        <TableCell align="right" sx={{ width: 130 }}>Amount</TableCell>
                        <TableCell sx={{ width: 160 }}>Recorded</TableCell>
                        <TableCell align="right" sx={{ width: 56 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {oneTimes.map(r => {
                        const isIncrease = r.direction === 'increase';
                        return (
                          <TableRow key={r.id} sx={{ '& td': { fontSize: 12, py: 0.7 } }}>
                            <TableCell sx={{ fontWeight: 700 }}>{r.student_name || '-'}</TableCell>
                            <TableCell>{r.student_id}</TableCell>
                            <TableCell>
                              {r.class_name
                                ? <Chip size="small" label={r.class_name} variant="outlined"
                                    sx={{ height: 20, fontWeight: 700 }} />
                                : '-'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>{r.account_name}</TableCell>
                            <TableCell>
                              <Chip size="small"
                                color={isIncrease ? 'warning' : 'success'} variant="outlined"
                                label={isIncrease ? 'Increase' : 'Write-off'}
                                sx={{ height: 20, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell align="right" sx={TNUM}>
                              {Number(r.schol_percent) > 0
                                ? `${Number(r.schol_percent).toFixed(2)}%`
                                : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{
                              ...TNUM, color: isIncrease ? 'warning.main' : 'success.main',
                              fontWeight: 800,
                            }}>
                              {isIncrease ? '+' : '-'}GHS {Number(r.schol_amount || 0).toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                              {r.created_at
                                ? new Date(r.created_at).toLocaleString(undefined,
                                    { dateStyle: 'medium', timeStyle: 'short' })
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={isIncrease ? 'Reverse this increase' : 'Reverse this write-off'}>
                                <IconButton size="small" color="warning" onClick={() => reverseOneTime(r)}>
                                  <UndoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Box>
        )}
      </Card>

      <SchemeDialog open={editOpen} onClose={() => setEditOpen(false)}
        onSaved={() => { loadSchemes(); }} scheme={editScheme}
        individualAccounts={individualAccounts} />

      <EnrollDialog open={enrollOpen} onClose={() => setEnrollOpen(false)}
        onSaved={() => {
          loadSchemes();
          if (enrollScheme?.id) {
            // Refresh the in-row panel so newly-enrolled students show
            // immediately after save, and force the row open if it
            // wasn't already.
            loadSchemeEnrollments(enrollScheme.id);
            setExpanded(prev => ({ ...prev, [enrollScheme.id]: true }));
          }
        }}
        scheme={enrollScheme} classes={classOptions} />

      <OneTimeDialog open={oneTimeOpen} onClose={() => setOneTimeOpen(false)}
        onSaved={() => { loadOneTimes(); }} classes={classOptions} />

      <RemoveAccountDialog open={removeOpen} onClose={() => setRemoveOpen(false)}
        onSaved={() => { loadOneTimes(); }} classes={classOptions} />
    </Box>
  );
}
